import { useMemo } from 'react';
import { BorrowerContractualizationResponse } from 'api/quote';
import { useBorrowerGlobalInfos } from '../store/borrowerContractualizationStore';
import { useQuoteDataFromStore } from '../store/contractualizationStore';
import {
  QuoteContractualizationData,
  BorrowerContractualizationData,
  ContractualizationDataUnion
} from '../types/contractualizationData';

/**
 * Transforme le DTO Borrower API en BorrowerContractualizationData
 */
const mapBorrowerResponseToData = (
  dto: BorrowerContractualizationResponse
): BorrowerContractualizationData => ({
  flowType: 'borrower',
  quoteId: dto.quoteId,
  tunnelId: dto.tunnelId,
  folderIdentifier: dto.folderIdentifier ?? '',
  statusCode: dto.statusCode,
  quoteTabs: dto.quoteTabs,
  productOwner: dto.productOwner,
  insured: dto.insured,
  process: dto.process,
  pricing: dto.pricing,
  pricingLoans: dto.pricingLoans,
  medicalDocuments: dto.medicalDocuments,
  documents: dto.documents,
  idDossier: dto.idDossier,
  numeroAssure: dto.numeroAssure,
  partnerCode: dto.partnerCode,
  isContractStepStarted: dto.isContractStepStarted,
  isCasLemoine: dto.isCasLemoine
});

/**
 * Hook pour acceder aux donnees de contractualisation du flow Quote
 *
 * Source de verite: contractualizationStore.quoteData
 * Les donnees sont ecrites dans le store lors du fetch dans fetchSubscriptionProcess
 *
 * Accessible partout (dans ou hors du Provider, Panels inclus)
 */
export const useQuoteContractualizationData = ():
  | QuoteContractualizationData
  | undefined => {
  const storeData = useQuoteDataFromStore();
  return storeData ?? undefined;
};

/**
 * Hook pour acceder aux donnees de contractualisation du flow Borrower
 * Source: BorrowerContractualizationStore.globalInfos
 *
 * A utiliser dans BorrowerContractualizationStep et ses enfants
 */
export const useBorrowerContractualizationData = ():
  | BorrowerContractualizationData
  | undefined => {
  const globalInfos = useBorrowerGlobalInfos();

  return useMemo(() => {
    if (!globalInfos) return undefined;
    return mapBorrowerResponseToData(globalInfos);
  }, [globalInfos]);
};

/**
 * Type du contexte de flow pour determiner quel hook utiliser
 */
export type ContractualizationFlowType = 'quote' | 'borrower';

/**
 * Hook generique qui retourne les donnees selon le type de flow
 * Utile pour les composants partages entre les deux flows
 *
 * @param flowType - 'quote' ou 'borrower'
 */
export const useContractualizationData = (
  flowType: ContractualizationFlowType
): ContractualizationDataUnion | undefined => {
  const quoteData = useQuoteContractualizationData();
  const borrowerData = useBorrowerContractualizationData();

  return flowType === 'quote' ? quoteData : borrowerData;
};

// ============================================
// Selecteurs derives pour le flow Quote
// Ces hooks permettent d'acceder aux champs specifiques du DTO
// ============================================

/**
 * Retourne l'ID du quote courant
 */
export const useQuoteId = (): string | undefined => {
  const data = useQuoteContractualizationData();
  return data?.quoteId;
};

/**
 * Retourne le riskId
 */
export const useRiskId = (): string | undefined => {
  const data = useQuoteContractualizationData();
  return data?.riskId;
};

/**
 * Retourne le branchCode
 */
export const useBranchCode = (): string | undefined => {
  const data = useQuoteContractualizationData();
  return data?.branchCode;
};

/**
 * Retourne les informations de l'assure
 */
export const useInsured = () => {
  const data = useQuoteContractualizationData();
  return data?.insured;
};
